import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements, PaymentRequestButtonElement } from '@stripe/react-stripe-js';

import { config } from '../../utils/config';
import ClubbieLoader from '../../components/Loader/ClubbieLoader';
import { teamRegistrationPaid } from '../../utils/analytics';

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(config.STRIPE_PUBLISHABLE_API_KEY || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Card Element styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      '::placeholder': {
        color: '#7F8585',
      },
      backgroundColor: 'transparent',
    },
    invalid: {
      color: '#ff4444',
      iconColor: '#ff4444',
    },
  },
};

// Payment Request Hook for Apple Pay / Google Pay
const usePaymentRequest = ({ stripe, amount, currency, onSuccess, onError }) => {
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);

  useEffect(() => {
    if (!stripe || !amount) {
      return;
    }

    const pr = stripe.paymentRequest({
      country: 'GB',
      currency: currency.toLowerCase(),
      total: {
        label: 'Payment',
        amount: Math.round(amount * 100), // Convert to pence
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    pr.canMakePayment().then((result) => {
      if (result) {
        setPaymentRequest(pr);
        setCanMakePayment(true);
      }
    });

    pr.on('paymentmethod', async (e) => {
      try {
        if (onSuccess) {
          await onSuccess(e.paymentMethod);
          e.complete('success');
        }
      } catch (error) {
        e.complete('fail');
        if (onError) {
          onError(error.message || 'Payment failed');
        }
      }
    });

    return () => {
      if (pr) {
        pr.off('paymentmethod');
      }
    };
  }, [stripe, amount, currency, onSuccess, onError]);

  return { paymentRequest, canMakePayment };
};

// Payment Form Component (uses Stripe hooks)
const PaymentForm = ({ payment, teamId, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  // Setup Payment Request for Apple Pay / Google Pay
  const { paymentRequest, canMakePayment } = usePaymentRequest({
    stripe,
    amount: payment.amount,
    currency: payment.currency || 'gbp',
    onSuccess: async (paymentMethod) => {
      setProcessing(true);
      try {
        const accessString = localStorage.getItem('access');
        const token = accessString ? JSON.parse(accessString).token : null;

        const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/payments/stripe-pay-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            paymentIds: [payment.id],
            teamId: teamId
          })
        });

        if (!response.ok) {
          throw new Error('Failed to create payment intent');
        }

        const paymentIntent = await response.json();

        const { error: confirmError } = await stripe.confirmCardPayment(
          paymentIntent.client_secret,
          { payment_method: paymentMethod.id }
        );

        if (confirmError) {
          throw confirmError;
        }

        onSuccess(paymentIntent);
      } catch (error) {
        onError(error.message);
      } finally {
        setProcessing(false);
      }
    },
    onError: onError,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    try {
      const accessString = localStorage.getItem('access');
      const token = accessString ? JSON.parse(accessString).token : null;

      // Create payment intent on backend
      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/payments/stripe-pay-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          paymentIds: [payment.id],
          teamId: teamId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create payment intent');
      }

      const paymentIntent = await response.json();

      // If payment requires confirmation, confirm with card
      if (paymentIntent.status === 'requires_payment_method' || paymentIntent.status === 'requires_confirmation') {
        const { error, paymentIntent: confirmedPayment } = await stripe.confirmCardPayment(
          paymentIntent.client_secret,
          {
            payment_method: {
              card: elements.getElement(CardElement),
            },
          }
        );

        if (error) {
          throw new Error(error.message);
        }

        if (confirmedPayment.status === 'succeeded') {
          onSuccess(confirmedPayment);
        }
      } else if (paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent);
      }
    } catch (error) {
      onError(error.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Apple Pay / Google Pay Button */}
      {canMakePayment && paymentRequest && (
        <div className="mb-4">
          <PaymentRequestButtonElement
            options={{
              paymentRequest,
              style: {
                paymentRequestButton: {
                  type: 'default',
                  theme: 'dark',
                  height: '48px',
                },
              },
            }}
          />
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#192634]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-[#011224] text-[#7F8585]">Or pay with card</span>
            </div>
          </div>
        </div>
      )}

      {/* Card Payment Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-[#FEFCEB] text-sm mb-3">Card details</label>
          <div className="bg-[#082441] rounded-lg p-4 border border-[#192634]">
            <CardElement
              options={cardElementOptions}
              onChange={(e) => setCardComplete(e.complete)}
            />
          </div>
          <p className="text-[#7F8585] text-xs mt-2 flex items-center gap-1">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 1v4m0 4v4M1 7h4m4 0h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            Your payment is secured by Stripe
          </p>
        </div>

        <button
          type="submit"
          disabled={!stripe || processing || !cardComplete}
          className="w-full bg-[#00E7FF] hover:bg-[#00d4e6] text-[#000F1F] px-6 py-3 rounded-full font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {processing ? (
            <>
              <div className="w-5 h-5 border-2 border-[#000F1F] border-t-transparent rounded-full animate-spin"></div>
              Processing...
            </>
          ) : (
            <>Pay £{payment.amount.toFixed(2)}</>
          )}
        </button>

        {/* Developer Mock Success Button */}
        {(process.env.NODE_ENV === 'development' || true) && (
          <button
            type="button"
            onClick={() => onSuccess({ status: 'succeeded', id: 'mock_pi_123' })}
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-2 rounded-full font-bold transition-colors flex items-center justify-center gap-2 mt-2"
          >
            DEBUG: Mock Success (Skip Stripe)
          </button>
        )}
      </form>
    </div>
  );
};

// Main Participant Payment Page
const ParticipantPayment = ({ embedded = false, teamId: teamIdProp }) => {
  const navigate = useNavigate();
  const params = useParams();
  const teamId = teamIdProp || params.teamId;
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [authRequired, setAuthRequired] = useState(false);
  const [payments, setPayments] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [team, setTeam] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    status: 'All Status',
    paymentType: 'All Types',
    dateFrom: '',
    dateTo: ''
  });

  const [feeBreakdown, setFeeBreakdown] = useState({
    stripeFee: 0,
    clubbieFee: 0,
    teamWillReceive: 0,
  });

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');

  useEffect(() => {
    fetchMyPayments();
    fetchTeamDetails();
  }, [teamId]);

  useEffect(() => {
    if (!showPaymentModal || !selectedPayment) return;
    // calculate fees for breakdown UI
    const fetchFees = async () => {
      try {
        const accessString = localStorage.getItem('access');
        const token = accessString ? JSON.parse(accessString).token : null;
        const response = await fetch(
          `${config.API_BASE_URL}/${config.API_PATH}/payments/fetch-amount-fees?amount=${selectedPayment.amount}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) return;
        const data = await response.json();
        setFeeBreakdown({
          stripeFee: parseFloat(data.stripeFee) || 0,
          clubbieFee: parseFloat(data.clubbieFee) || 0,
          teamWillReceive: parseFloat(data.sentToTeamBankAccount) || 0,
        });
      } catch (e) {
      }
    };
    fetchFees();
  }, [showPaymentModal, selectedPayment]);

  const fetchTeamDetails = async () => {
    try {
      const accessString = localStorage.getItem('access');
      const token = accessString ? JSON.parse(accessString).token : null;

      if (!token) return;

      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/teams/${teamId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTeam(data);
      }
    } catch (error) {
    }
  };

  const fetchMyPayments = async () => {
    setLoading(true);
    try {
      const accessString = localStorage.getItem('access');
      const token = accessString ? JSON.parse(accessString).token : null;
      const userString = localStorage.getItem('user');
      const user = userString ? JSON.parse(userString) : null;

      // In embedded mode (team dashboard), we try to fetch even if user isn't traditionally logged in
      // The dashboard itself handles auth via invitation token
      if (!embedded && (!token || !user)) {
        setAuthRequired(true);
        setPayments([]);
        return;
      }

      // If we have a token, use it; otherwise for embedded mode, continue anyway
      // The API will return appropriate error if not authorized
      if (!token && !embedded) {
        setAuthRequired(true);
        setPayments([]);
        return;
      }

      setAuthRequired(false);

      // In embedded mode (team dashboard), fetch payments where this team is the TARGET (payer)
      // Otherwise, fetch payments for this user in the team
      let apiUrl;
      if (embedded) {
        // Team owner dashboard: fetch payments created for this team
        apiUrl = `${config.API_BASE_URL}/${config.API_PATH}/payments?targetTeamId=${teamId}`;
      } else {
        // Regular user view: fetch user's own payments in the team
        apiUrl = `${config.API_BASE_URL}/${config.API_PATH}/payments?teamId=${teamId}&userId=${user.id}`;
      }

      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(apiUrl, { headers });

      if (!response.ok) {
        if (response.status === 401) {
          setAuthRequired(true);
          setPayments([]);
          return;
        }
        throw new Error('Failed to fetch payments');
      }

      const data = await response.json();
      setPayments(data.results || []);
    } catch (error) {
      if (embedded) {
        // In embedded mode, just show empty payments on error
        setPayments([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = (payment) => {
    setSelectedPayment(payment);
    setShowPaymentModal(true);
    setPaymentError('');
    setSelectedPaymentMethod('card');
  };

  const handlePaymentSuccess = (paymentIntent) => {
    try {
      const userString = localStorage.getItem('user');
      const storedUser = userString ? JSON.parse(userString) : null;
      teamRegistrationPaid({
        tournamentId:
          selectedPayment?.tournamentId ||
          selectedPayment?.competitionId ||
          selectedPayment?.tournament?.id ||
          selectedPayment?.tournament?._id,
        teamId,
        amount: selectedPayment?.amount,
        paymentId: paymentIntent?.id || selectedPayment?._id || selectedPayment?.id,
        user: storedUser,
      });
    } catch (_e) {}
    setPaymentSuccess(true);
    setShowPaymentModal(false);
    fetchMyPayments(); // Refresh the list
  };

  const handlePaymentError = (errorMessage) => {
    setPaymentError(errorMessage);
  };

  const handlePayByCash = async (paymentId) => {
    try {
      const accessString = localStorage.getItem('access');
      const token = accessString ? JSON.parse(accessString).token : null;

      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/payments/cash-paid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          paymentIds: [paymentId],
          teamId: teamId
        })
      });

      if (!response.ok) throw new Error('Failed to request cash payment');

      alert('Cash payment request sent. Waiting for approval.');
      fetchMyPayments();
    } catch (error) {
      alert('Failed to request cash payment. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'paid': { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30', label: 'Paid' },
      'unpaid': { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', label: 'Unpaid' },
      'pending': { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30', label: 'Processing' },
      'pending-cash-approval': { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30', label: 'Awaiting approval' },
      'canceled': { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30', label: 'Canceled' }
    };

    const config = statusConfig[status] || statusConfig.unpaid;

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}>
        {config.label}
      </span>
    );
  };

  const unpaidPayments = payments.filter(p => p.status === 'unpaid');
  const pendingPayments = payments.filter(p => ['pending', 'pending-cash-approval'].includes(p.status));
  const paidPayments = payments.filter(p => p.status === 'paid');

  const totals = {
    totalReceived: paidPayments.reduce((sum, p) => sum + (p.amount || 0), 0),
    totalPending: pendingPayments.reduce((sum, p) => sum + (p.amount || 0), 0),
    totalDue: unpaidPayments.reduce((sum, p) => sum + (p.amount || 0), 0),
  };

  const getPaymentTitle = (payment) => {
    if (payment.payFor === 'Custom') return payment.customReference || 'Custom payment';
    if (payment.payFor === 'Event') return payment.payReference?.title || 'Event payment';
    return 'Subscription';
  };

  const parseLineItems = (customReference) => {
    if (!customReference) return [];
    const items = customReference.split(', ');
    return items
      .map((item) => {
        const match = item.match(/^(.+):\s*£(\d+\.?\d*)$/);
        if (match) return { description: match[1], amount: parseFloat(match[2]) };
        return null;
      })
      .filter(Boolean);
  };

  const filteredPayments = payments.filter((p) => {
    const matchesSearch = !filters.search || getPaymentTitle(p).toLowerCase().includes(filters.search.toLowerCase());
    const matchesStatus = filters.status === 'All Status' || p.status === filters.status;
    const matchesType = filters.paymentType === 'All Types' || p.payFor === filters.paymentType;

    let matchesDate = true;
    if (filters.dateFrom) {
      matchesDate = matchesDate && new Date(p.createdAt) >= new Date(filters.dateFrom);
    }
    if (filters.dateTo) {
      const endDate = new Date(filters.dateTo);
      endDate.setHours(23, 59, 59, 999); // Include the whole end day
      matchesDate = matchesDate && new Date(p.createdAt) <= endDate;
    }

    return matchesSearch && matchesStatus && matchesType && matchesDate;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000F1F] flex items-center justify-center">
        <ClubbieLoader />
      </div>
    );
  }

  if (authRequired) {
    return (
      <div className={embedded ? "text-[#FEFCEB]" : "min-h-screen bg-[#000F1F] text-[#FEFCEB]"}>
        <div className={embedded ? "" : "max-w-[1200px] mx-auto py-6 px-6"}>
          <div className="bg-[#011224] rounded-2xl border border-[#192634] p-8">
            <h2 className="text-[#FEFCEB] font-semibold text-lg mb-2">Sign in required</h2>
            <p className="text-[#7F8585] mb-6">
              Please sign in to view and pay your team payments.
            </p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="bg-[#00E7FF] hover:bg-[#00d4e6] text-[#000F1F] px-6 py-3 rounded-full font-semibold transition-colors"
            >
              Sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  const content = (
    <>
      {!embedded && (
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-[#7F8585] hover:text-[#FEFCEB] transition-colors mb-4"
          >
            ← Back
          </button>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-3xl font-bold text-[#FEFCEB] mb-1">Payment</h1>
              {team && (
                <div className="flex items-center gap-3">
                  {team.crest && (
                    <div className="w-10 h-10 rounded-full bg-cover bg-center" style={{ backgroundImage: `url(${team.crest})` }} />
                  )}
                  <div>
                    <p className="text-[#FEFCEB] font-medium leading-tight">{team.title}</p>
                    <p className="text-[#7F8585] text-sm leading-tight">Owner: {team?.owner?.profile?.fullName || team?.owner?.profile?.firstName || 'N/A'}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Match totals */}
      <div className="mb-6">
        <h2 className="text-[#FEFCEB] font-semibold mb-4">Match totals</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-[#082441] rounded-xl border border-[#192634] p-6">
            <span className="block text-[#AEB9E1] text-sm mb-2">Total received</span>
            <span className="text-[#FEFCEB] text-3xl font-semibold">£ {totals.totalReceived.toLocaleString()}</span>
          </div>
          <div className="bg-[#082441] rounded-xl border border-[#192634] p-6">
            <span className="block text-[#AEB9E1] text-sm mb-2">Total pending</span>
            <span className="text-[#FEFCEB] text-3xl font-semibold">£ {totals.totalPending.toLocaleString()}</span>
          </div>
          <div className="bg-[#082441] rounded-xl border border-[#192634] p-6">
            <span className="block text-[#AEB9E1] text-sm mb-2">Total due</span>
            <span className="text-[#FEFCEB] text-3xl font-semibold">£ {totals.totalDue.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#011224] rounded-2xl border border-[#192634] p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          <div className="md:col-span-4">
            <div className="relative">
              <input
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Search payments..."
                className="w-full bg-[#082441] text-[#FEFCEB] rounded-lg pl-10 pr-4 py-2.5 border border-[#192634] outline-none focus:border-[#00E7FF] placeholder-[#7F8585]"
              />
              <svg className="absolute left-4 top-1/2 -translate-y-1/2" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M7 12A5 5 0 107 2a5 5 0 000 10z" stroke="#7F8585" strokeWidth="1.5" />
                <path d="M11 11l3 3" stroke="#7F8585" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
          </div>

          <div className="md:col-span-2">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-2.5 border border-[#192634] outline-none focus:border-[#00E7FF]"
            >
              <option value="All Status">All Status</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <select
              value={filters.paymentType}
              onChange={(e) => setFilters({ ...filters, paymentType: e.target.value })}
              className="w-full bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-2.5 border border-[#192634] outline-none focus:border-[#00E7FF]"
            >
              <option value="All Types">All Types</option>
              <option value="Custom">Custom</option>
              <option value="Event">Event</option>
              <option value="Subscription">Subscription</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <input
              type="date"
              value={filters.dateFrom || ''}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              className="w-full bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-2.5 border border-[#192634] outline-none focus:border-[#00E7FF] text-sm [&::-webkit-calendar-picker-indicator]:invert"
              placeholder="From"
            />
          </div>

          <div className="md:col-span-2">
            <input
              type="date"
              value={filters.dateTo || ''}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              className="w-full bg-[#082441] text-[#FEFCEB] rounded-lg px-4 py-2.5 border border-[#192634] outline-none focus:border-[#00E7FF] text-sm [&::-webkit-calendar-picker-indicator]:invert"
              placeholder="To"
            />
          </div>
        </div>
      </div>

      {/* Success Message Toast */}
      {paymentSuccess && (
        <div className="fixed top-24 right-6 z-50 animate-in slide-in-from-right-full duration-300">
          <div className="bg-[#001224] border border-green-500/30 rounded-xl p-4 shadow-2xl flex items-center gap-4 min-w-[320px]">
            <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center shrink-0">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M16 6L8 14L4 10" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-[#FEFCEB] font-semibold text-sm">Payment successful!</h3>
              <p className="text-[#7F8585] text-xs mt-0.5">Your payment has been processed successfully.</p>
            </div>
            <button
              onClick={() => setPaymentSuccess(false)}
              className="text-[#7F8585] hover:text-[#FEFCEB] transition-colors p-1"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Payments grid */}
      {filteredPayments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPayments.map((payment) => {
            const statusLabel = payment.status;
            return (
              <div key={payment.id} className="bg-[#011224] rounded-2xl border border-[#192634] p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-[#FEFCEB] font-semibold">{getPaymentTitle(payment)}</h3>
                    <p className="text-[#7F8585] text-xs mt-1">
                      Next payment(s) due {new Date(payment.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </p>
                    {payment.status === 'pending-cash-approval' && (
                      <p className="text-[#7F8585] text-xs mt-1">I've already paid in cash</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-[#FEFCEB] font-semibold">£ {Number(payment.amount || 0).toFixed(0)}</p>
                    <div className="mt-1">{getStatusBadge(statusLabel)}</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-4">
                  <button
                    onClick={() => handlePayNow(payment)}
                    className="flex-1 bg-[#082441] hover:bg-[#0a2f5c] text-[#FEFCEB] rounded-full py-2.5 border border-[#192634] transition-colors"
                  >
                    Pay with card
                  </button>
                  <button
                    onClick={() => handlePayNow(payment)}
                    className="bg-white text-black rounded-full px-5 py-2.5 font-medium"
                    title="Pay"
                  >
                     Pay
                  </button>
                </div>

                {payment.status === 'unpaid' && (
                  <button
                    onClick={() => handlePayByCash(payment.id)}
                    className="mt-3 text-[#7F8585] hover:text-[#FEFCEB] text-xs transition-colors underline"
                    type="button"
                  >
                    Notify organizer of cash payment
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-[#011224] rounded-2xl border border-[#192634] p-12 text-center">
          <div className="w-16 h-16 bg-[#082441] rounded-full flex items-center justify-center mx-auto mb-4">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <path d="M16 2v12m0 4v12M2 16h12m4 0h12" stroke="#7F8585" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <h3 className="text-[#FEFCEB] font-semibold text-lg mb-2">No payments</h3>
          <p className="text-[#7F8585]">You don't have any payments assigned to you yet.</p>
        </div>
      )}
    </>
  );

  return (
    <div className={embedded ? "text-[#FEFCEB]" : "min-h-screen bg-[#000F1F] text-[#FEFCEB]"}>
      <div className={embedded ? "" : "max-w-[1200px] mx-auto py-6 px-6"}>{content}</div>

      {/* Payment Modal */}
      {showPaymentModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#000F1F] rounded-2xl border-2 border-[#192634] p-6 max-w-5xl w-full">
            <div className="flex items-center justify-between mb-6">
              <div>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="text-[#7F8585] hover:text-[#FEFCEB] transition-colors text-sm mb-2"
                  type="button"
                >
                  ← Back to Home page
                </button>
                <h2 className="text-xl font-bold text-[#FEFCEB]">Select payment method</h2>
              </div>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-[#7F8585] hover:text-[#FEFCEB] transition-colors text-2xl"
                type="button"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: payment method + card */}
              <div className="bg-[#011224] rounded-2xl border border-[#192634] p-6">
                <div className="mb-4">
                  <label className="block text-[#FEFCEB] text-sm mb-3">Payment method</label>
                  {paymentError && (
                    <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 mb-4">
                      <p className="text-red-400 text-sm">{paymentError}</p>
                    </div>
                  )}
                  <Elements stripe={stripePromise}>
                    <PaymentForm
                      payment={selectedPayment}
                      teamId={teamId}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                    />
                  </Elements>
                </div>
              </div>

              {/* Right: breakdown */}
              <div className="bg-[#011224] rounded-2xl border border-[#192634] p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[#FEFCEB] font-semibold">Payment breakdown</h3>
                  <button type="button" className="text-[#7F8585] hover:text-[#FEFCEB]">…</button>
                </div>

                <div className="space-y-2 text-sm">
                  {parseLineItems(selectedPayment.customReference).length > 0 ? (
                    parseLineItems(selectedPayment.customReference).map((item, idx) => (
                      <div key={idx} className="flex justify-between text-[#FEFCEB]">
                        <span>{item.description}</span>
                        <span>£ {Number(item.amount).toFixed(2)}</span>
                      </div>
                    ))
                  ) : (
                    <div className="flex justify-between text-[#FEFCEB]">
                      <span>{getPaymentTitle(selectedPayment)}</span>
                      <span>£ {Number(selectedPayment.amount || 0).toFixed(2)}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-[#7F8585] pt-2">
                    <span>Stripe fee (1.5% + 20p)</span>
                    <span>£ {feeBreakdown.stripeFee.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[#7F8585]">
                    <span>Clubbie fee (2%)</span>
                    <span>£ {feeBreakdown.clubbieFee.toFixed(2)}</span>
                  </div>

                  <div className="border-t border-[#192634] pt-3 mt-3">
                    <div className="flex justify-between text-[#FEFCEB] font-semibold">
                      <span>To be paid by player</span>
                      <span>£ {Number(selectedPayment.amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-[#00E7FF] font-semibold">
                      <span>Team will receive</span>
                      <span>£ {feeBreakdown.teamWillReceive.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParticipantPayment;
